import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useEstateContext } from './EstateContext';
import { base44 } from '@/legacy/base44Client';
import { isIframe } from '@/lib/utils';
import { pagesConfig } from '@/pages.config';
import { capturePageView, identifyUser } from '@/lib/analytics';

export default function NavigationTracker() {
    const location = useLocation();
    
    // Safely get auth state - handle case where AuthProvider isn't mounted yet
    let isAuthenticated = false;
    let user = null;
    let activeEstateId = null;
    try {
        const auth = useAuth();
        const estateCtx = useEstateContext();
        isAuthenticated = auth?.isAuthenticated || false;
        user = auth?.user || null;
        activeEstateId = estateCtx?.activeEstateId || null;
    } catch (error) {
        // AuthProvider not ready yet (hot reload), skip auth checks
        console.log('⚠️ NavigationTracker: AuthProvider not ready');
    }
    
    const { Pages, mainPage } = pagesConfig;
    const mainPageKey = mainPage ?? Object.keys(Pages)[0];

    // Post navigation changes to parent window (only when running inside an iframe)
    useEffect(() => {
        if (!isIframe) return;
        window.parent?.postMessage({
            type: "app_changed_url",
            url: window.location.href
        }, '*');
    }, [location]);

    useEffect(() => {
        capturePageView({
            pathname: location.pathname,
            search: location.search,
            title: document.title,
        });
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!user?.id) return;

        identifyUser(user.id, {
            email: user.email,
            user_role: user.user_role,
            estate_id: activeEstateId,
        });
    }, [user?.id, user?.email, user?.user_role, activeEstateId]);

    // Log user activity when navigating to a page
    useEffect(() => {
        // Skip if not authenticated
        if (!isAuthenticated) return;

        // Extract page name from pathname
        const pathname = location.pathname;
        let pageName;
        
        if (pathname === '/' || pathname === '') {
            pageName = mainPageKey;
        } else {
            // Remove leading slash and get the first segment
            const pathSegment = pathname.replace(/^\//, '').split('/')[0];
            
            // Try case-insensitive lookup in Pages config
            const pageKeys = Object.keys(Pages);
            const matchedKey = pageKeys.find(
                key => key.toLowerCase() === pathSegment.toLowerCase()
            );
            
            pageName = matchedKey || null;
        }

        // Only log if we have a valid page name and the logging function exists
        if (pageName && base44?.appLogs?.logUserInApp) {
            base44.appLogs.logUserInApp(pageName).catch((error) => {
                // Silently fail - logging shouldn't break the app
                console.debug('Navigation logging skipped:', error.message);
            });
        } else if (pageName) {
            console.debug('📍 Navigation:', pageName, '(logging disabled during migration)');
        }
    }, [location, isAuthenticated, Pages, mainPageKey]);

    return null;
}